<template>
  <div class="max-w-[720px] w-full px-4 sm:px-0 sm:mr-5">
    <h2 class="w-full text-center mb-8 mr-4">
      {{ $t('messages.menu.two_factor_auth') }}
    </h2>
    <div class="list--account flex flex-col border border-gray-200 bg-white rounded-lg p-4 min-h-[480px] sm:ml-10">
      <div class="flex justify-between items-center pb-4 border-b border-zyxel-gray-300">
        <div class="flex-grow mr-4">
          {{ $t('messages.two_factor_auth.setup') }}
        </div>
        <label
          class="relative inline-flex items-center cursor-pointer"
          :class="{ 'cursor-not-allowed': isDemoAccount }"
        >
          <input
            v-model="is_enable"
            type="checkbox"
            class="sr-only peer"
            :disabled="isDemoAccount"
            aria-label="Toggle 2FA auth"
            @change="handleToggleChange"
          >
          <div
            class="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-zyxel-green-300 dark:peer-focus:ring-zyxel-green-500 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-zyxel-green-500 outline-none
                            "
          />
          <span class="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300" />
        </label>
      </div>
      <div
        v-if="!is_enable"
        class="flex flex-col items-center w-full flex-grow"
      >
        <img
          src="@/assets/svg/img-img_2fa.svg"
          alt="2FA auth icon"
          class="h-[160px] w-[160px] mt-[60px] mb-8"
        >
        <div class="b2 text-zyxel-gray-800 mt-4 text-center">
          {{ $t('messages.two_factor_auth.disabled')
          }}
        </div>
      </div>
      <div v-else>
        <div
          :class="{ 'bg-zyxel-gray-200': auth_type !== 0, 'border-zyxel-green-500 border-2': auth_type === 0 }"
          class="rounded-lg bg-white mx-4 mt-4 border border-zyxel-gray-500 text-left w-auto"
          role="button"
          tabindex="0"
          @click="handleSwitch2FA(0)"
          @keydown.enter="handleSwitch2FA(0)"
          @keydown.space="handleSwitch2FA(0)"
        >
          <div class="hidden sm:flex">
            <img
              class="w-12 h-12 m-4 mt-6"
              src="@/assets/svg/icon_48px_Google2FA.svg"
              alt="Google 2FA"
            >
            <div class="flex flex-col w-full mr-6">
              <div
                :class="{ 'border-b': auth_type === 0 }"
                class="flex justify-center items-center w-full"
              >
                <div class="flex flex-col mt-8 mb-4 w-full">
                  <h3>{{ $t('messages.two_factor_auth.google_title') }}</h3>
                  <p class="b2 text-zyxel-gray-800">
                    {{
                      $t('messages.two_factor_auth.google_description')
                    }}
                  </p>
                </div>
                <div>
                  <img
                    v-if="auth_type === 0"
                    class="w-8 h-8 mr-8"
                    src="@/assets/svg/icon_48px_tickCircle_Green.svg"
                    alt="Setting complete"
                  >
                  <div
                    v-else
                    class="w-5 h-5 bg-white border border-gray-300 rounded-full m-6"
                  />
                </div>
              </div>
              <div
                v-if="auth_type === 0"
                class="my-4"
              >
                <div class="b1 my-4">
                  {{ $t('messages.two_factor_auth.backup_code_title') }}
                </div>
                <p class="b2 text-zyxel-gray-800 mb-4">
                  {{
                    $t('messages.two_factor_auth.backup_code_description') }}
                </p>
                <div class="bg-zyxel-gray-900 p-12 rounded mb-4 flex justify-center items-center">
                  <div
                    class="max-w-[360px] grid grid-flow-row auto-rows-max grid-cols-[repeat(auto-fill,_minmax(60px,_1fr))] gap-2"
                  >
                    <div
                      v-for="( code, index ) in otp.otp_backup_codes "
                      :key="index"
                      class="text-white text-center"
                    >
                      {{ code }}
                    </div>
                  </div>
                </div>
                <div class="flex items-center">
                  <button
                    class="b2 text-zyxel-green-700 cursor-pointer py-2 px-2"
                    @click="handleDownloadBackupCodes"
                  >
                    {{ $t("messages.two_factor_auth.backup_code_download") }}
                  </button>
                  <span class="b2 text-zyxel-green-700 ml-1 mr-1">|</span>
                  <button
                    class="b2 text-zyxel-green-700 cursor-pointer py-2 px-2"
                    @click="handleGenerateNewBackupCodes"
                  >
                    {{ $t("messages.two_factor_auth.backup_code_generate") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="flex-col sm:hidden">
            <div class="flex justify-between">
              <img
                class="w-12 h-12 m-2 "
                src="@/assets/svg/icon_48px_Google2FA.svg"
                alt="Google 2FA"
              >
              <div>
                <img
                  v-if="auth_type === 0"
                  class="w-8 h-8 mt-4 mr-5"
                  src="@/assets/svg/icon_48px_tickCircle_Green.svg"
                  alt="Setting complete"
                >
                <div
                  v-else
                  class="w-5 h-5 bg-white border border-gray-300 rounded-full m-6"
                />
              </div>
            </div>
            <div class="flex flex-col w-full p-4">
              <div
                :class="{ 'border-b': auth_type === 0 }"
                class="flex justify-center items-center w-full"
              >
                <div class="flex flex-col mb-4 w-full">
                  <h3>{{ $t('messages.two_factor_auth.google_title') }}</h3>
                  <p class="b2 text-zyxel-gray-800">
                    {{
                      $t('messages.two_factor_auth.google_description')
                    }}
                  </p>
                </div>
              </div>
              <div
                v-if="auth_type === 0"
                class="my-4"
              >
                <div class="b1 my-4">
                  {{ $t('messages.two_factor_auth.backup_code_title') }}
                </div>
                <p class="b2 text-zyxel-gray-800 mb-4">
                  {{
                    $t('messages.two_factor_auth.backup_code_description') }}
                </p>
                <div class="bg-zyxel-gray-900 p-12 rounded mb-4 flex justify-center items-center">
                  <div
                    class="max-w-[180px] grid grid-flow-row auto-rows-max grid-cols-[repeat(auto-fill,_minmax(60px,_1fr))] gap-2"
                  >
                    <div
                      v-for="( code, index ) in otp.otp_backup_codes "
                      :key="index"
                      class="text-white text-center"
                    >
                      {{ code }}
                    </div>
                  </div>
                </div>
                <div class="flex items-center">
                  <button
                    class="b2 text-zyxel-green-700 cursor-pointer py-2 px-2"
                    @click="handleDownloadBackupCodes"
                  >
                    {{ $t("messages.two_factor_auth.backup_code_download") }}
                  </button>
                  <span class="b2 text-zyxel-green-700 ml-1 mr-1">|</span>
                  <button
                    class="b2 text-zyxel-green-700 cursor-pointer py-2 px-2"
                    @click="handleGenerateNewBackupCodes"
                  >
                    {{ $t("messages.two_factor_auth.backup_code_generate") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          :class="{ 'bg-zyxel-gray-200': auth_type !== 1, 'border-zyxel-green-500 border-2': auth_type === 1 }"
          class="rounded-lg bg-white mx-4 mt-4 border border-zyxel-gray-500 text-left w-auto"
          role="button"
          tabindex="0"
          @click="handleSwitch2FA(1)"
          @keydown.enter="handleSwitch2FA(1)"
          @keydown.space="handleSwitch2FA(1)"
        >
          <div class="hidden sm:flex">
            <img
              class="w-12 h-12 m-4"
              src="@/assets/svg/icon_48px_email.svg"
              alt="Email 2FA"
            >
            <div class="flex flex-col w-full mr-6">
              <div
                :class="{ 'border-b': auth_type === 1 }"
                class="flex justify-center items-center w-full"
              >
                <div class="flex flex-col mt-8 mb-4 w-full">
                  <h3>{{ $t("messages.two_factor_auth.email_auth_title") }}</h3>
                  <p class="b2 text-zyxel-gray-800">
                    {{
                      $t("messages.two_factor_auth.email_auth_description")
                    }}
                  </p>
                </div>
                <div>
                  <img
                    v-if="auth_type === 1"
                    class="w-8 h-8 mr-8"
                    src="@/assets/svg/icon_48px_tickCircle_Green.svg"
                    alt="Setting complete"
                  >
                  <div
                    v-else
                    class="w-5 h-5 bg-white border border-gray-300 rounded-full m-6"
                  />
                </div>
              </div>
              <div
                v-if="auth_type === 1"
                class="my-4"
              >
                <div class="bg-zyxel-gray-900 p-4 rounded">
                  <div class="b3 text-white">
                    {{ $t('messages.two_factor_auth.main_mail')
                    }}
                  </div>
                  <p class="b2 text-white mb-4">
                    {{ user.email }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="flex-col sm:hidden">
            <div class="flex justify-between">
              <img
                class="w-12 h-12 m-2 "
                src="@/assets/svg/icon_48px_email.svg"
                alt="Email 2FA"
              >
              <div>
                <img
                  v-if="auth_type === 1"
                  class="w-8 h-8 mt-4 mr-5"
                  src="@/assets/svg/icon_48px_tickCircle_Green.svg"
                  alt="Setting complete"
                >
                <div
                  v-else
                  class="w-5 h-5 bg-white border border-gray-300 rounded-full m-6"
                />
              </div>
            </div>
            <div class="flex flex-col w-full p-4">
              <div
                :class="{ 'border-b': auth_type === 0 }"
                class="flex justify-center items-center w-full"
              >
                <div class="flex flex-col mb-4 w-full">
                  <h3>{{ $t('messages.two_factor_auth.email_auth_title') }}</h3>
                  <p class="b2 text-zyxel-gray-800">
                    {{
                      $t('messages.two_factor_auth.email_auth_description')
                    }}
                  </p>
                </div>
              </div>
              <div
                v-if="auth_type === 1"
                class="my-4"
              >
                <div class="bg-zyxel-gray-900 p-4 rounded">
                  <div class="b3 text-white">
                    {{ $t('messages.two_factor_auth.main_mail')
                    }}
                  </div>
                  <p class="b2 text-white mb-4">
                    {{ user.email }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <TwoFactorAuthModal
    ref="twoFactorAuthModal"
    :show-modal="showModal"
    :user="user"
    :otp="otp"
    @update-otp-backup-codes="otp.otp_backup_codes = $event"
    @close-modal="handleCloseModal"
  />
  <SendEmail2FAModal
    :show-modal="showSendEmail2FAModal"
    :user="user"
    @close-modal="handleCloseSendEmailModal"
  />
  <Switch2FAModal
    :show-modal="showSwitchModal"
    :target_auth_type="target_auth_type"
    @close-modal="handleCloseSwitchModal"
    @switch2fa="switchAuthType"
  />
  <Disable2FAModal
    :show-modal="showDisableModal"
    :user="user"
    @close-modal="handleCloseDisableModal"
    @reset-otp-verified="resetOtpVerifiedInModal"
  />
  <Enable2FADialog
    :show-modal="showEnable2FADialog"
    :user="user"
    @close-modal="handleCloseEnableEmail2FADialog"
  />
  <Loading
    v-model:active="isLoading"
    color="#3C9F00"
    loader="spinner"
    background-color="#FFFFFF"
    :is-full-page="true"
  />
</template>

<script>
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import TwoFactorAuthModal from '@/components/TwoFactorAuthModal.vue';
import Switch2FAModal from '@/components/Switch2FAModal.vue';
import Disable2FAModal from '@/components/Disable2FAModal.vue';
import SendEmail2FAModal from '@/components/SendEmail2FAModal.vue';
import Enable2FADialog from "@/components/Enable2FADialog.vue";
import { downloadBackupCodes, generateNewBackupCodes } from '@/otpHelper.js';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import { logger } from '@/utils/logger';


export default {
    name: 'TwoFactorAuthSetting',
    components: {
        TwoFactorAuthModal,
        Switch2FAModal,
        Disable2FAModal,
        SendEmail2FAModal,
        Enable2FADialog,
        Loading,
    },
    setup() {
        const showEnable2FADialog = ref(false);
        const route = useRoute();
        const enableEmail = route.query.enableEmail === 'true';
        if (enableEmail) {
            showEnable2FADialog.value = true;
        }
        const handleCloseEnableEmail2FADialog = () => {
            showEnable2FADialog.value = false;
            const redirectUri = localStorage.getItem('redirect_uri');
            if (redirectUri) {
                //this flow must to take care redirect
                const auth = useAuthStore();
                auth.getAuthorizationCodeAndRedirect();
            }
        };

        return {
            showEnable2FADialog,
            handleCloseEnableEmail2FADialog,
        };
    },
    data() {
        return {
            is_enable: false,
            auth_type: 0,
            target_auth_type: 0,
            isLoading: false,
            showModal: false,
            showSwitchModal: false,
            showDisableModal: false,
            showSendEmail2FAModal: false,
            otp: {
            },
        }
    },
    computed: {
        isDemoAccount() {
            const authStore = useAuthStore();
            return authStore.isDemoAccount;
        },
        user() {
            const authStore = useAuthStore();
            return authStore.current_user || {
                first_name: 'N/A',
                last_name: 'N/A',
                email: 'no-email@example.com',
                country: 'TW',
                contactPerson: 'None'
            };
        }
    },
    created() {
        this.fetchSettings();
    },
    methods: {
        async fetchSettings() {
            const authStore = useAuthStore();
            try {
                this.isLoading = true;
                const settings = await authStore.get2FASettings();

                this.is_enable = settings.is_enable;
                this.auth_type = settings.auth_type;
                this.otp = settings.otp;
            } catch (error) {
                logger.error('Error fetching 2FA settings:', error);
            } finally {
                this.isLoading = false;
            }
        },
        async fetchOtpSettings() {
            const authStore = useAuthStore();
            try {
                const otpSettings = await authStore.getOtpSettings();
                this.otp = otpSettings.otp;
            } catch (error) {
                logger.error('Error fetching OTP settings:', error);
            }
        },
        async handleDownloadBackupCodes() {
            await downloadBackupCodes(this.otp);
        },
        async handleGenerateNewBackupCodes() {
            const new_backup_codes = await generateNewBackupCodes();
            if (new_backup_codes) {
                this.otp.otp_backup_codes = new_backup_codes;
            }
        },
        async handleCloseModal() {
            this.showModal = false;
            document.body.classList.remove('modal-open');
            await this.fetchSettings();
        },
        async handleCloseSendEmailModal() {
            this.showSendEmail2FAModal = false;
            await this.fetchSettings();
        },
        async handleCloseDisableModal() {
            this.showDisableModal = false;
            await this.fetchSettings();
        },
        async handleToggleChange() {
            await this.fetchOtpSettings();
            if (this.is_enable) {
                this.showModal = true;
                document.body.classList.add('modal-open');
            } else {
                this.showDisableModal = true;
            }
        },
        async handleSwitch2FA(target_auth_type) {
            if (this.auth_type != target_auth_type) {
                this.target_auth_type = target_auth_type;
                this.showSwitchModal = true;
            }
        },
        async switchAuthType() {
            const auth = useAuthStore();
            if (this.target_auth_type === 0) {
                await this.fetchOtpSettings();
                this.showSwitchModal = false;
                this.showModal = true;
                document.body.classList.add('modal-open');
            }
            if (this.target_auth_type === 1) {
                await auth.send2FAEmailVerification();
                this.showSwitchModal = false;
                this.showSendEmail2FAModal = true;
            }
        },
        handleCloseSwitchModal() {
            this.showSwitchModal = false;
            this.fetchSettings();
        },
        resetOtpVerifiedInModal() {
            this.otp={};
            this.$refs.twoFactorAuthModal.closeOtpVerified();
        }
    },
}
</script>
<style>
/* 在你的全域 CSS 文件或 style 標籤中加入 */
body.modal-open {
  overflow: hidden;
}
</style>